/** Hub personas for "view as" role dropdown and role → permissions map. */
export type HubAppRole = "consumer" | "producer" | "admin";

const HUB_APP_ROLE_COOKIE_NAME = "hubAppRole";
const HUB_APP_ROLE_MAX_AGE_SEC = 60 * 60 * 24 * 400;
const HUB_APP_ROLE_VALUES = new Set<string>(["consumer", "producer", "admin"]);

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

/** Last selected DXP Hub persona for persistence across sessions. */
export function getHubAppRoleCookie(): HubAppRole | null {
  const raw = getCookie(HUB_APP_ROLE_COOKIE_NAME);
  if (!raw || !HUB_APP_ROLE_VALUES.has(raw)) return null;
  return raw as HubAppRole;
}

export function setHubAppRoleCookie(role: HubAppRole): void {
  if (typeof document === "undefined") return;
  const value = encodeURIComponent(role);
  // biome-ignore lint/suspicious/noDocumentCookie: mirror other client prefs (e.g. sidebar cookie)
  document.cookie = `${HUB_APP_ROLE_COOKIE_NAME}=${value}; path=/; max-age=${HUB_APP_ROLE_MAX_AGE_SEC}; SameSite=Lax`;
}

/** Response shape for role → permissions map (client-side until API ships). */
interface IHubRolePermissionsResponse {
  roles: Record<HubAppRole, string[]>;
}

export const HARDCODED_HUB_ROLE_PERMISSIONS: IHubRolePermissionsResponse = {
  roles: {
    consumer: [],
    producer: [
      "catalog.view",
      "catalog.buy",
      "catalog.create",
      "catalog.edit",
      "catalog.type.manage",
      "catalog.review.view",
      "settings.view",
      "cdv.manage",
      "package.manage",
    ],
    admin: [
      "catalog.view",
      "catalog.buy",
      "catalog.create",
      "catalog.edit",
      "catalog.type.manage",
      "catalog.review.view",
      "catalog.review.participate",
      "catalog.transfer",
      "settings.view",
      "cdv.manage",
      "package.manage",
      "package.transfer",
      "administer.access",
      "subscription.manage",
    ],
  },
};

export const HARDCODED_PORTAL_PERMISSIONS = {
  roles: {
    STANDARD_USER: [],
    COMPANY_ADMIN: [
      "settings.view",
      "subscription.manage",
      "administer.access",
    ],
    APP_ADMIN: ["settings.view", "subscription.manage", "administer.access"],
    INTERNAL: [
      "catalog.view",
      "catalog.buy",
      "catalog.create",
      "catalog.edit",
      "catalog.type.manage",
      "catalog.review.view",
      "catalog.review.participate",
      "catalog.transfer",
      "settings.view",
      "cdv.manage",
      "package.manage",
      "package.transfer",
      "administer.access",
      "subscription.manage",
      "dashboard.access",
    ],
  },
};

export const HUB_PERM = {
  CATALOG_VIEW: "catalog.view",
  CATALOG_BUY: "catalog.buy",
  CATALOG_CREATE: "catalog.create",
  CATALOG_EDIT: "catalog.edit",
  CATALOG_TYPE_MANAGE: "catalog.type.manage",
  CATALOG_REVIEW_PARTICIPATE: "catalog.review.participate",
  CATALOG_REVIEW_VIEW: "catalog.review.view",
  CATALOG_TRANSFER: "catalog.transfer",
  SETTINGS_VIEW: "settings.view",
  CDV_MANAGE: "cdv.manage",
  PACKAGE_MANAGE: "package.manage",
  PACKAGE_TRANSFER: "package.transfer",
  ADMINISTER_ACCESS: "administer.access",
  SUBSCRIPTION_MANAGE: "subscription.manage",
  DASHBOARD_ACCESS: "dashboard.access",
} as const;
